import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef, useState } from 'react';

export const RESIZE_HANDLE_DWELL_MS = 400;

type HoverDwell = {
    readonly isRevealed: boolean;
    readonly onPointerLeave: () => void;
    readonly onPointerMove: () => void;
    readonly reveal: () => void;
};

// Reveals only once the pointer has rested for `delayMs`; every move restarts the wait, so sweeping across shows nothing.
export const useHoverDwell = (delayMs: number = RESIZE_HANDLE_DWELL_MS): HoverDwell => {
    const [isRevealed, setIsRevealed] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const clearTimer = useCallback((): void => {
        if (!is.undefined(timerRef.current)) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    useEffect(() => clearTimer, [clearTimer]);

    const reveal = useCallback((): void => {
        clearTimer();
        setIsRevealed(true);
    }, [clearTimer]);

    const onPointerMove = useCallback((): void => {
        if (isRevealed) {
            return;
        }

        clearTimer();
        timerRef.current = setTimeout(() => {
            timerRef.current = undefined;
            setIsRevealed(true);
        }, delayMs);
    }, [clearTimer, delayMs, isRevealed]);

    const onPointerLeave = useCallback((): void => {
        clearTimer();
        setIsRevealed(false);
    }, [clearTimer]);

    return { isRevealed, onPointerLeave, onPointerMove, reveal };
};
